import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { checkBackendStatus } from "../actions/backendVersionActions";
import { checkRunningTests } from "../hooks/useCheckRunningTests";
import {
  shouldPollVersion,
  shouldPollRunning,
  TestWorkflowState,
  TestWorkflowAction,
} from "../constants/testWorkflowState";
import { useAppWorkflowContext } from "./AppWorkflowContext";
import { useAuthContext } from "./AuthContext";
import { useCognitoContext } from "./CognitoContext";

const PollingManagerContext = createContext(null);

export const PollingManagerProvider = ({ children }) => {
  const { workflowState, dispatchWorkflow } = useAppWorkflowContext();
  const { isAuthenticated } = useAuthContext();
  const { signOut } = useCognitoContext();

  const [backendStatus, setBackendStatus] = useState(null);
  const [runningState, setRunningState] = useState(null);

  const versionIntervalRef = useRef(null);
  const runningIntervalRef = useRef(null);

  const clearRef = (ref) => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  };

  // Load /version and /running once on app init
  useEffect(() => {
    if (workflowState !== TestWorkflowState.APP_INIT) return;
  
    Promise.all([checkBackendStatus(), checkRunningTests()])
      .then(([version, running]) => {
        setBackendStatus(version);
        setRunningState(running);
        if (version?.version === "Not able to read version.") {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });
          return;
        }

        if (running?.isTestNotFailedAndNotReported === false) {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_WAITING_FOR_PROCESSING });
        } else {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
        }
      })
      .catch(() => {
        const isUpdatePersisted = localStorage.getItem("showUpdateInProgressModal") === "true";
        if (isUpdatePersisted) {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });
          return;
        }
        dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
      });
    
  }, [workflowState]);

  // Poll /version based on workflow state
  useEffect(() => {
    if (!isAuthenticated) return;

    clearRef(versionIntervalRef);

    if (!shouldPollVersion(workflowState)) {
      return;
    }

    const intervalMs =
      workflowState === TestWorkflowState.BACKEND_UPDATING ? 2000 : 30000;

    versionIntervalRef.current = setInterval(() => {
      checkBackendStatus()
        .then((status) => {
          setBackendStatus(status);
        })
        .catch((err) => {
          console.error("[PollingManager] /version tick failed:", err)
          const isRebooting =
            localStorage.getItem("sonda.backend.rebooting") === "true";
          const isUpdateInProgress =
            localStorage.getItem("showUpdateInProgressModal") === "true";
  
          const isRebootingOrUpdating = isRebooting || isUpdateInProgress;
          if (
            err?.response?.status &&
            (err.response.status === 401 || err.response.status === 403) &&
            !isRebootingOrUpdating
          ) {
            signOut();
          }
          setBackendStatus(null);
        });
    }, intervalMs);

    return () => clearRef(versionIntervalRef);
  }, [workflowState, isAuthenticated]);

  // Poll /running only when WAITING_FOR_PROCESSING
  useEffect(() => {
    if (!isAuthenticated) return;

    clearRef(runningIntervalRef);

    if (!shouldPollRunning(workflowState)) {
      return;
    }

    const intervalMs = 2000;
    runningIntervalRef.current = setInterval(() => {
      checkRunningTests()
        .then((data) => {
          setRunningState(data);

          if (data?.isTestNotFailedAndNotReported === true) {
            clearRef(runningIntervalRef);
            dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
          }
        })
        .catch((err) => console.error("[PollingManager] /running tick failed:", err));
    }, intervalMs);

    return () => clearRef(runningIntervalRef);

  }, [workflowState, isAuthenticated]);

  const value = {
    backendStatus,
    runningState,
  };

  return (
    <PollingManagerContext.Provider value={value}>
      {children}
    </PollingManagerContext.Provider>
  );
};

export const usePollingManager = () => useContext(PollingManagerContext);